import { ReactNode, useRef } from "react";

import { Box, Collapse, Stack } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Control, FieldError, FieldErrors, FieldValues, Path, useWatch } from "react-hook-form";

import { AutomatedEmailOpenedCard } from "../types";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isMax, isMin, isRequired, isRequiredHtml } from "@/common/helpers/validators";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { countBlockedShortenerLinksInHtml } from "@/components/FullScreenEmailEditorContainer/FullScreenEmailBuilder/helpers/bodyValidationChecks";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { OutlinedMailFieldWysiwyg } from "@/components/OutlinedMailFieldWysiwyg/OutlinedMailFieldWysiwyg";
import {
  CustomFroalaConfig,
  ExtraWysiwygVariable,
  FroalaConfig,
  StaticVariableLabels,
} from "@/components/OutlinedTextFieldWysiwyg/type";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox";
import { DashboardChipVibe } from "@/dashboard-design-system/DashboardChip";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { DashboardSwitch } from "@/dashboard-design-system/Switch";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { TypographyVibes } from "@/dashboard-design-system/Typography/types";
import { ChevronDown, ChevronUp, Mail, Mobile } from "@/icons/outlined";

const EDITOR_MIN_HEIGHT = 160;

interface ChipConfig {
  label: string;
  vibe?: DashboardChipVibe;
  showWhen: "enabled";
  tooltip?: string;
}

interface OffsetUnitFieldConfig {
  name: string;
  options: { value: string; label: string }[];
}

interface OffsetConfig {
  name: string;
  suffixLabel: string;
  onBlur?: () => void;
  unitField?: OffsetUnitFieldConfig;
  maxOffset?: number;
}

interface BodyConfig {
  defaultValue?: string;
  attachmentFieldName?: string;
  extraVariables?: ExtraWysiwygVariable[];
  staticVariableLabels?: StaticVariableLabels;
  customConfig?: CustomFroalaConfig;
  readyToMount?: boolean;
}

interface ChannelIconsConfig {
  email: boolean;
  sms: boolean;
}

interface ChannelSelectorsConfig {
  email: { checked: boolean; locked?: boolean; onChange?: (value: boolean) => void };
  sms: { checked: boolean; onChange: (value: boolean) => void };
}

interface AutomatedCommunicationCardProps<T extends FieldValues> {
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  dataEmailType: AutomatedEmailOpenedCard;

  fields: {
    subject?: string;
    content: string;
  };

  enabled: boolean;
  onEnabledChange?: (next: boolean) => void;

  chip?: ChipConfig;
  chips?: ChipConfig[];
  offset?: OffsetConfig;
  body?: BodyConfig;

  lockedOn?: boolean;
  onBlur?: () => void;
  footnote?: ReactNode;
  collapse?: {
    isOpen: boolean;
    onToggle: () => void;
  };
  hideToggle?: boolean;
  toggleTooltip?: string;
  canBeOpened?: boolean;
  additionalActions?: ReactNode;
  disabledFieldsTooltip?: string;
  channelIcons?: ChannelIconsConfig;
  channelSelectors?: ChannelSelectorsConfig;
  transactionalNotice?: boolean;
  dataTestPrefix?: string;
}

export const AutomatedCommunicationCard = <T extends FieldValues>({
  label,
  control,
  errors,
  dataEmailType,
  fields,
  enabled,
  onEnabledChange,
  chip,
  chips,
  offset,
  body,
  lockedOn = false,
  onBlur,
  footnote,
  collapse,
  hideToggle = false,
  toggleTooltip,
  canBeOpened = true,
  additionalActions,
  disabledFieldsTooltip,
  channelIcons,
  channelSelectors,
  transactionalNotice,
  dataTestPrefix,
}: AutomatedCommunicationCardProps<T>) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();
  const subjectRef = useRef<HTMLInputElement | null>(null);

  const isOpen = collapse ? collapse.isOpen : true;
  const canCollapse = Boolean(collapse) && canBeOpened;

  const isEnabled = enabled;
  const isFieldsDisabled = Boolean(disabledFieldsTooltip);
  const labelVibe = isEnabled && !isFieldsDisabled ? undefined : "text-neutral-quiet";

  const isEmailActive = channelSelectors ? channelSelectors.email.checked : true;

  const headerChips = resolveHeaderChips(chip, chips);
  const showChip = headerChips.length > 0 && isEnabled;

  const getNestedError = (path: string): FieldError | undefined => {
    const parts = path.split(".");
    let current: unknown = errors;
    for (const part of parts) {
      if (!current || typeof current !== "object") {
        return undefined;
      }
      current = (current as Record<string, unknown>)[part];
    }
    return current as FieldError | undefined;
  };

  const subjectError = fields.subject ? getNestedError(fields.subject)?.message : undefined;
  const contentError = getNestedError(fields.content);
  const offsetError = offset ? getNestedError(offset.name)?.message : undefined;

  // Links to public URL shorteners (bit.ly, tinyurl.com, …) are rewritten by SendGrid and land
  // recipients on a "Not found" page, so warn the author before they save. Same detection the
  // bulk-email builder uses, run here on the raw WYSIWYG HTML.
  const contentHtml = useWatch({ control, name: fields.content as Path<T> });
  const blockedLinkShortenerCount = countBlockedShortenerLinksInHtml(contentHtml);

  const getRequiredRule = (fieldLabel: string, requiresEmailActive = false) => {
    if (!isEnabled || (requiresEmailActive && !isEmailActive)) {
      return [];
    }
    return [isRequired(fieldLabel)];
  };

  const getRequiredHtmlRule = (fieldLabel: string, requiresEmailActive = false) => {
    if (!isEnabled || (requiresEmailActive && !isEmailActive)) {
      return [];
    }
    return [isRequiredHtml(fieldLabel)];
  };

  return (
    <LiveEditorBlock data-email-type={dataEmailType}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        maxWidth="100%"
        onClick={canCollapse ? collapse?.onToggle : undefined}
        sx={{ cursor: canCollapse ? "pointer" : "default" }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" paddingRight={1} sx={{ flex: 1, minWidth: 0 }}>
          {!hideToggle && (
            <Stack onClick={(e) => e.stopPropagation()} justifyContent="center">
              <DashboardTooltip title={disabledFieldsTooltip || toggleTooltip}>
                <span>
                  <DashboardSwitch
                    checked={enabled}
                    disabled={lockedOn || isFieldsDisabled}
                    onChange={(_, checked) => onEnabledChange?.(checked)}
                    data-test={dataTestPrefix ? `${dataTestPrefix}-toggle` : undefined}
                  />
                </span>
              </DashboardTooltip>
            </Stack>
          )}

          <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
            {label}
          </DashboardTypography>
        </Stack>

        <Stack gap={2} direction="row" alignItems="center">
          {showChip && !isSmallScreen && <HeaderChips chips={headerChips} />}
          {channelIcons && isEnabled && (channelIcons.email || channelIcons.sms) && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={(theme) => ({ fill: theme.palette.text.neutral.moderate })}
            >
              {channelIcons.email && <Mail width={16} height={16} />}
              {channelIcons.sms && <Mobile width={16} height={16} />}
            </Stack>
          )}

          {additionalActions && (
            <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
              {additionalActions}
            </Box>
          )}

          {canCollapse && (
            <DashboardIconButton
              onClick={(e) => {
                e.stopPropagation();
                collapse?.onToggle();
              }}
              vibe="neutral"
              size="small"
            >
              {isOpen ? <ChevronUp /> : <ChevronDown />}
            </DashboardIconButton>
          )}
        </Stack>
      </Stack>

      {showChip && isSmallScreen && (
        <Box>
          <HeaderChips chips={headerChips} />
        </Box>
      )}

      {canBeOpened && (
        <ContentWrapper canCollapse={canCollapse} isOpen={isOpen}>
          <Box marginTop={3}>
            <Stack rowGap={1.5}>
              {channelSelectors && (
                <ChannelSelectorsRow
                  channelSelectors={channelSelectors}
                  isEnabled={isEnabled}
                  labelVibe={labelVibe}
                  dataTestPrefix={dataTestPrefix}
                />
              )}
              {transactionalNotice && (
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", "ticketing.automatedCommunications.outbid.transactionalNotice")}
                </DashboardTypography>
              )}
              {offset && (
                <Stack marginBottom={2}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <DashboardTypography variant="body2" vibe={labelVibe}>
                      {t("dashboard", "ticketing.setThankYouEmail.reminder.send")}
                    </DashboardTypography>
                    <Box sx={{ width: 56 }}>
                      <DashboardTextField
                        data-test={dataTestPrefix ? `${dataTestPrefix}-offset-input` : undefined}
                        name={offset.name as Path<T>}
                        control={control}
                        type="number"
                        size="small"
                        disabled={!isEnabled || isFieldsDisabled}
                        onBlur={offset.onBlur}
                        rules={[
                          ...getRequiredRule(t("dashboard", "ticketing.setThankYouEmail.reminder.offsetRequired")),
                          isMin(1, t("dashboard", "ticketing.setThankYouEmail.reminder.offsetMinError", { min: 1 })),
                          isMax(
                            offset.maxOffset ?? 100,
                            t("dashboard", "ticketing.setThankYouEmail.reminder.offsetMaxError", {
                              max: offset.maxOffset ?? 100,
                            })
                          ),
                        ]}
                      />
                    </Box>
                    {offset.unitField && (
                      <Box sx={{ width: 100 }}>
                        <DashboardSelect
                          data-test={dataTestPrefix ? `${dataTestPrefix}-offset-unit` : undefined}
                          name={offset.unitField.name as Path<T>}
                          control={control}
                          size="small"
                          disabled={!isEnabled || isFieldsDisabled}
                        >
                          {offset.unitField.options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </DashboardSelect>
                      </Box>
                    )}
                    <DashboardTypography variant="body2" vibe={labelVibe}>
                      {offset.suffixLabel}
                    </DashboardTypography>
                  </Stack>
                  {offsetError && (
                    <DashboardTypography variant="caption" vibe="text-danger-quiet" sx={{ marginTop: 0.5 }}>
                      {offsetError}
                    </DashboardTypography>
                  )}
                </Stack>
              )}

              {body && !transactionalNotice && (
                <Collapse in={isEmailActive} unmountOnExit>
                  <DashboardTooltip title={isFieldsDisabled && hideToggle ? disabledFieldsTooltip : undefined}>
                    <Stack rowGap={1}>
                      {fields.subject && (
                        <DashboardTextField
                          data-test={dataTestPrefix ? `${dataTestPrefix}-subject-input` : undefined}
                          name={fields.subject as Path<T>}
                          label={t("dashboard", "ticketingAndDonationForm.formEmailSubject")}
                          control={control}
                          rules={getRequiredRule(
                            t("dashboard", "ticketingAndDonationForm.formEmailSubjectRequired"),
                            true
                          )}
                          noAsterisk={!isEnabled || lockedOn}
                          errorMessage={subjectError}
                          disabled={!isEnabled || isFieldsDisabled}
                          onBlur={onBlur}
                        />
                      )}

                      {body?.readyToMount === false ? (
                        <DashboardSkeleton
                          variant="rectangular"
                          width="100%"
                          height={EDITOR_MIN_HEIGHT}
                          sx={{ borderRadius: 1 }}
                        />
                      ) : (
                        <OutlinedMailFieldWysiwyg
                          data-test={dataTestPrefix ? `${dataTestPrefix}-body-input` : undefined}
                          attachmentKeyName={body?.attachmentFieldName}
                          name={fields.content}
                          subjectRef={subjectRef}
                          subjectName={fields.subject ?? fields.content}
                          error={contentError}
                          rules={getRequiredHtmlRule(
                            t("dashboard", "ticketingAndDonationForm.formEmailBodyRequired"),
                            true
                          )}
                          isoLocale={isoLocale}
                          defaultValue={body?.defaultValue ?? ""}
                          heightMinOver310={EDITOR_MIN_HEIGHT}
                          disabled={!isEnabled || isFieldsDisabled}
                          config={FroalaConfig.Email}
                          customConfig={body?.customConfig}
                          extraVariables={body?.extraVariables}
                          staticVariableLabels={body?.staticVariableLabels}
                          onBlur={onBlur}
                        />
                      )}
                      <BlockedShortenerWarning count={blockedLinkShortenerCount} dataTestPrefix={dataTestPrefix} />
                    </Stack>
                  </DashboardTooltip>
                </Collapse>
              )}
            </Stack>
            {footnote}
          </Box>
        </ContentWrapper>
      )}
    </LiveEditorBlock>
  );
};

const ContentWrapper = ({
  canCollapse,
  isOpen,
  children,
}: {
  canCollapse: boolean;
  isOpen: boolean;
  children: ReactNode;
}) => {
  if (!canCollapse) {
    return <>{children}</>;
  }
  return <Collapse in={isOpen}>{children}</Collapse>;
};

const ChannelSelectorsRow = ({
  channelSelectors,
  isEnabled,
  labelVibe,
  dataTestPrefix,
}: {
  channelSelectors: ChannelSelectorsConfig;
  isEnabled: boolean;
  labelVibe: TypographyVibes | undefined;
  dataTestPrefix?: string;
}) => {
  const { t } = useTranslate();
  return (
    <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
      <DashboardTypography variant="body2" vibe={labelVibe}>
        {t("dashboard", "ticketing.automatedCommunications.sendVia")}
      </DashboardTypography>
      <DashboardCheckbox
        checked={channelSelectors.email.checked}
        disabled={channelSelectors.email.locked || !isEnabled}
        onChange={(_, checked) => channelSelectors.email.onChange?.(checked)}
        label={t("dashboard", "ticketing.automatedCommunications.channel.email")}
        size="small"
        data-test={dataTestPrefix ? `${dataTestPrefix}-channel-email` : undefined}
      />
      <DashboardCheckbox
        checked={channelSelectors.sms.checked}
        onChange={(_, checked) => channelSelectors.sms.onChange(checked)}
        label={t("dashboard", "ticketing.automatedCommunications.channel.sms")}
        size="small"
        disabled={!isEnabled}
        data-test={dataTestPrefix ? `${dataTestPrefix}-channel-sms` : undefined}
      />
    </Stack>
  );
};

const BlockedShortenerWarning = ({ count, dataTestPrefix }: { count: number; dataTestPrefix?: string }) => {
  const { t } = useTranslate();
  if (count <= 0) {
    return null;
  }
  return (
    <DashboardInfobox
      vibe="warning"
      size="small"
      sx={{ width: "100%" }}
      data-test={dataTestPrefix ? `${dataTestPrefix}-blocked-shortener-warning` : undefined}
    >
      {t("dashboard", "ticketing.automatedCommunications.blockedLinkShortenerWarning", { count })}
    </DashboardInfobox>
  );
};

const resolveHeaderChips = (chip?: ChipConfig, chips?: ChipConfig[]): ChipConfig[] => chips ?? (chip ? [chip] : []);

const HeaderChips = ({ chips }: { chips: ChipConfig[] }) => (
  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
    {chips.map((chip) => (
      <ChipWithTooltip key={chip.label} chip={chip} />
    ))}
  </Stack>
);

const ChipWithTooltip = ({ chip }: { chip: ChipConfig }) => {
  const chipElement = <DashboardChip label={chip.label} variant="filled" vibe={chip.vibe} shape="rounded" />;
  if (!chip.tooltip) {
    return chipElement;
  }
  return (
    <DashboardTooltip
      title={chip.tooltip}
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line", textAlign: "left", maxWidth: 360 } } }}
    >
      <span>{chipElement}</span>
    </DashboardTooltip>
  );
};
